import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaArrowLeft,
  FaSpinner,
  FaUsers,
  FaTrash,
  FaExclamationTriangle,
} from "react-icons/fa";
import useApp from "../../hooks/useAppContext";
import { Link, useParams, useNavigate } from "react-router-dom";
import ModalB from "react-bootstrap/Modal";
import ModalInscritosAgenda from "../../components/agenda/ModalInscritosAgenda";
import ModalPreviewPatrocinadoresCaviar from "../../components/agenda/ModalPreviewPatrocinadoresCaviar";
import useAuth from "../../hooks/useAuthContext";
import api from "../../services/axios";
import { toast } from "react-toastify";
import "./admPatrocinadoresList.css";
import "./adm.css";

/** Agendas de degustação de caviar para participantes patrocinadores. */
const AGENDAS_CAVIAR_PATROCINADOR = [6, 7, 8];
const CAVIAR_PATROCINADOR_MAX_VAGAS = 50;

const LABELS_TIPO = {
  geral: "Geral",
  conteudo_patrocinado: "Conteúdo patrocinado",
  conteudo_editorial: "Conteúdo editorial",
  esporte: "Esporte",
  reuniao_negocio: "Reunião de negócio",
  extraordinaria: "Extraordinária",
  acompanhante: "Acompanhante",
  kids: "Kids",
  m_m: "Match & Meet",
};

function horarioEvento(valor) {
  if (!valor || typeof valor !== "string" || valor.length < 16) return "—";
  return valor.substring(11, 16);
}

function qtdInscritos(evento) {
  return (evento?.participantes || []).length;
}

function ordenarParticipantes(lista) {
  return [...(lista || [])].sort((a, b) => {
    const nomeA = a?.usuario?.nome || "";
    const nomeB = b?.usuario?.nome || "";
    return nomeA.localeCompare(nomeB, "pt-BR");
  });
}

const TIPOS_SEM_ACOES_EM_MASSA = [
  "conteudo_editorial",
  "conteudo_patrocinado",
  "extraordinaria",
];

const AdmEventos = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const { getEventos, getDiasEvento } = useApp();
  const { dia } = useParams();

  const [eventos, setEventos] = useState([]);
  const [diasEvento, setDiasEvento] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDias, setIsLoadingDias] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [eventoModal, setEventoModal] = useState(null);
  const [participantesModal, setParticipantesModal] = useState([]);
  const [acaoModalLoading, setAcaoModalLoading] = useState(false);
  const [showConfirmExcluirTodos, setShowConfirmExcluirTodos] = useState(false);
  const [showPreviewCaviar, setShowPreviewCaviar] = useState(false);
  const [previewCaviar, setPreviewCaviar] = useState(null);
  const [previewCaviarLoading, setPreviewCaviarLoading] = useState(false);
  const [confirmCaviarLoading, setConfirmCaviarLoading] = useState(false);

  const isAdmin = usuario?.tipo === "administracao";
  const semAcoesEmMassa = TIPOS_SEM_ACOES_EM_MASSA.includes(eventoModal?.tipo);
  const vagasCaviarDisponiveis =
    CAVIAR_PATROCINADOR_MAX_VAGAS - participantesModal.length;
  const podeIncluirPatrocinadoresCaviar =
    isAdmin &&
    eventoModal?.tipo === "extraordinaria" &&
    AGENDAS_CAVIAR_PATROCINADOR.includes(Number(eventoModal?.id)) &&
    vagasCaviarDisponiveis > 0;

  const carregarDias = useCallback(async () => {
    setIsLoadingDias(true);
    try {
      const dados = await getDiasEvento();
      const dias = dados?.dias || [];
      setDiasEvento(dias);
      return dias;
    } catch (err) {
      console.error("Erro ao carregar dias do evento:", err);
      setError("Erro ao carregar dias do evento");
      return [];
    } finally {
      setIsLoadingDias(false);
    }
  }, [getDiasEvento]);

  const carregarEventos = useCallback(async () => {
    if (!dia) return [];

    setIsLoading(true);
    setError(null);
    try {
      const auxEventos = await getEventos(dia);
      const lista = auxEventos || [];
      setEventos(lista);
      return lista;
    } catch (err) {
      console.error("Erro ao carregar eventos:", err);
      setError("Erro ao carregar lista de conteúdos e esportes");
      setEventos([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [dia, getEventos]);

  useEffect(() => {
    const iniciar = async () => {
      const dias = await carregarDias();
      if (!dia && dias.length > 0) {
        navigate(`/private/adm/eventos/${dias[0].dia}`, { replace: true });
      }
    };
    iniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregarDias]);

  useEffect(() => {
    carregarEventos();
  }, [carregarEventos]);

  const eventosFiltrados = useMemo(() => {
    if (!filtro.trim()) return eventos;
    const termo = filtro.toLowerCase().trim();
    return eventos.filter((evento) => {
      const campos = [
        evento.nome,
        evento.tipo,
        LABELS_TIPO[evento.tipo],
        evento.local,
        horarioEvento(evento.dt_hora_inicio),
        horarioEvento(evento.dt_hora_fim),
      ];
      return campos.some((campo) =>
        String(campo || "")
          .toLowerCase()
          .includes(termo)
      );
    });
  }, [eventos, filtro]);

  const indicadores = useMemo(
    () => ({
      total: eventos.length,
    }),
    [eventos]
  );

  const diaAtivoLabel = useMemo(() => {
    const encontrado = diasEvento.find((item) => item.dia === dia);
    return encontrado?.label || dia || "—";
  }, [diasEvento, dia]);

  const sincronizarModalComEventos = useCallback((listaEventos, idAgenda) => {
    const atualizado = (listaEventos || []).find((e) => e.id === idAgenda);
    if (!atualizado) {
      setShowModal(false);
      setEventoModal(null);
      setParticipantesModal([]);
      return;
    }
    setEventoModal(atualizado);
    setParticipantesModal(ordenarParticipantes(atualizado.participantes));
  }, []);

  const handleModalInscritos = (item) => {
    setEventoModal(item);
    setParticipantesModal(ordenarParticipantes(item.participantes));
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setEventoModal(null);
    setParticipantesModal([]);
    setAcaoModalLoading(false);
    setShowConfirmExcluirTodos(false);
    setShowPreviewCaviar(false);
    setPreviewCaviar(null);
    setPreviewCaviarLoading(false);
    setConfirmCaviarLoading(false);
  };

  const handleAbrirPreviewPatrocinadoresCaviar = async () => {
    if (!eventoModal?.id || acaoModalLoading || previewCaviarLoading) return;

    setShowPreviewCaviar(true);
    setPreviewCaviar(null);
    setPreviewCaviarLoading(true);
    try {
      const resp = await api.post("/agenda-patrocinadores-caviar-preview", {
        id_agenda: eventoModal.id,
      });
      setPreviewCaviar(resp.data?.dados || null);
    } catch (err) {
      toast.error(
        err?.response?.data?.mensagem ||
          err?.response?.data?.erro ||
          "Erro ao gerar prévia de participantes patrocinadores."
      );
      setShowPreviewCaviar(false);
    } finally {
      setPreviewCaviarLoading(false);
    }
  };

  const handleConfirmarPatrocinadoresCaviar = async () => {
    if (!eventoModal?.id || confirmCaviarLoading) return;

    setConfirmCaviarLoading(true);
    try {
      const resp = await api.post("/agenda-patrocinadores-caviar-confirmar", {
        id_agenda: eventoModal.id,
      });
      const adicionados = resp.data?.dados?.adicionados ?? 0;
      toast.success(
        resp.data?.mensagem ||
          `${adicionados} participante(s) patrocinador(es) incluído(s).`
      );
      setShowPreviewCaviar(false);
      setPreviewCaviar(null);
      const lista = await carregarEventos();
      sincronizarModalComEventos(lista, eventoModal.id);
    } catch (err) {
      toast.error(
        err?.response?.data?.mensagem ||
          err?.response?.data?.erro ||
          "Erro ao confirmar inclusão de participantes patrocinadores."
      );
    } finally {
      setConfirmCaviarLoading(false);
    }
  };

  const handleAdicionarTodos = async () => {
    if (!eventoModal?.id || acaoModalLoading) return;

    const confirmar = window.confirm(
      "Adicionar este conteúdo a todos os usuários não deletados?\nQuem já estiver inscrito será ignorado."
    );
    if (!confirmar) return;

    setAcaoModalLoading(true);
    try {
      const resp = await api.post("/agenda-participantes-todos", {
        id_agenda: eventoModal.id,
      });
      const dados = resp.data?.dados || {};
      toast.success(
        `Adicionados: ${dados.adicionados ?? 0}. Já inscritos: ${
          dados.ja_inscritos ?? 0
        }.`
      );
      const lista = await carregarEventos();
      sincronizarModalComEventos(lista, eventoModal.id);
    } catch (err) {
      toast.error(
        err?.response?.data?.mensagem || "Erro ao adicionar inscritos."
      );
    } finally {
      setAcaoModalLoading(false);
    }
  };

  const handleRemoverParticipante = async (participante) => {
    if (!eventoModal?.id || !participante?.usuario?.id || acaoModalLoading) {
      return;
    }

    const nome = participante.usuario?.nome || "este participante";
    const confirmar = window.confirm(
      `Remover ${nome} deste conteúdo?\nO evento será mantido.`
    );
    if (!confirmar) return;

    setAcaoModalLoading(true);
    try {
      await api.post("/agenda-participante-remover", {
        id_agenda: eventoModal.id,
        id_usuario: participante.usuario.id,
      });
      toast.success("Participante removido.");
      const lista = await carregarEventos();
      sincronizarModalComEventos(lista, eventoModal.id);
    } catch (err) {
      toast.error(
        err?.response?.data?.mensagem || "Erro ao remover participante."
      );
    } finally {
      setAcaoModalLoading(false);
    }
  };

  const abrirConfirmExcluirTodos = () => {
    if (!eventoModal?.id || acaoModalLoading || participantesModal.length === 0) {
      return;
    }
    setShowConfirmExcluirTodos(true);
  };

  const confirmarRemoverTodos = async () => {
    if (!eventoModal?.id || acaoModalLoading) return;

    setAcaoModalLoading(true);
    try {
      const resp = await api.post("/agenda-participantes-remover-todos", {
        id_agenda: eventoModal.id,
      });
      toast.success(
        `Removidos: ${resp.data?.dados?.removidos ?? 0} participante(s).`
      );
      setShowConfirmExcluirTodos(false);
      const lista = await carregarEventos();
      sincronizarModalComEventos(lista, eventoModal.id);
    } catch (err) {
      toast.error(
        err?.response?.data?.mensagem || "Erro ao remover participantes."
      );
    } finally {
      setAcaoModalLoading(false);
    }
  };

  return (
    <div className="container-adm-main">
      <ModalB
        show={showConfirmExcluirTodos}
        onHide={() => !acaoModalLoading && setShowConfirmExcluirTodos(false)}
        centered
        className="agenda-confirm-excluir-modal"
        backdrop={acaoModalLoading ? "static" : true}
      >
        <ModalB.Header closeButton={!acaoModalLoading}>
          <ModalB.Title className="agenda-confirm-excluir-title">
            <FaExclamationTriangle
              size={18}
              className="agenda-confirm-excluir-icon"
              aria-hidden
            />
            Confirmar exclusão
          </ModalB.Title>
        </ModalB.Header>
        <ModalB.Body>
          <p className="mb-2">
            Remover <strong>todos</strong> os participantes inscritos do
            conteúdo <strong>{eventoModal?.nome || "selecionado"}</strong>?
          </p>
          <p className="mb-0 text-muted">
            Serão removidos{" "}
            <strong>{participantesModal.length}</strong> participante(s)
            inscrito(s). O evento será mantido.
          </p>
        </ModalB.Body>
        <ModalB.Footer className="agenda-confirm-excluir-footer">
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setShowConfirmExcluirTodos(false)}
            disabled={acaoModalLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="apl-btn agenda-confirm-excluir-btn"
            onClick={confirmarRemoverTodos}
            disabled={acaoModalLoading}
          >
            {acaoModalLoading ? (
              <FaSpinner className="fa-spin" size={14} aria-hidden />
            ) : (
              <FaTrash size={14} aria-hidden />
            )}
            {acaoModalLoading ? "Excluindo…" : "Excluir todos"}
          </button>
        </ModalB.Footer>
      </ModalB>

      <ModalInscritosAgenda
        show={showModal}
        onClose={fecharModal}
        titulo={`Inscritos — ${eventoModal?.nome || "Evento"}`}
        participantes={participantesModal}
        imprimirTo={
          eventoModal?.id
            ? `/private/adm/conteudo/imprimir/${eventoModal.id}`
            : undefined
        }
        isAdmin={isAdmin}
        loading={acaoModalLoading || previewCaviarLoading || confirmCaviarLoading}
        onRemoverParticipante={handleRemoverParticipante}
        onAdicionarTodos={semAcoesEmMassa ? undefined : handleAdicionarTodos}
        onExcluirTodos={semAcoesEmMassa ? undefined : abrirConfirmExcluirTodos}
        onIncluirPatrocinadoresCaviar={
          podeIncluirPatrocinadoresCaviar
            ? handleAbrirPreviewPatrocinadoresCaviar
            : undefined
        }
        emptyHintSemInscritos={
          semAcoesEmMassa
            ? "Ainda não há participantes inscritos neste conteúdo."
            : 'Use “Adicionar a todos” para incluir os convidados.'
        }
      />

      <ModalPreviewPatrocinadoresCaviar
        show={showPreviewCaviar}
        onClose={() => {
          if (confirmCaviarLoading) return;
          setShowPreviewCaviar(false);
          setPreviewCaviar(null);
        }}
        onConfirmar={handleConfirmarPatrocinadoresCaviar}
        preview={previewCaviar}
        loading={previewCaviarLoading}
        confirmando={confirmCaviarLoading}
      />

      <div className="apl-page">
        <header className="apl-header" aria-label="Cabeçalho da listagem">
          <div className="apl-header-text">
            <h1>Conteúdos e Esportes</h1>
            <p>
              Consulte conteúdos e atividades esportivas por dia do evento,
              veja inscritos e edite a programação.
            </p>
          </div>

          <div className="apl-toolbar apl-toolbar--inline">
            <div className="apl-search">
              <span className="apl-search-icon" aria-hidden>
                <FaSearch size={14} />
              </span>
              <input
                type="search"
                className="apl-search-input"
                placeholder="Buscar por horário, evento ou tipo…"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                disabled={isLoading || isLoadingDias}
                aria-label="Buscar conteúdo ou esporte"
              />
            </div>

            <div className="apl-toolbar-actions">
              {isAdmin && (
                <Link
                  to="/private/adm/eventos-novo"
                  className="apl-btn apl-btn--primary"
                >
                  <FaPlus size={14} aria-hidden />
                  Novo evento
                </Link>
              )}

              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
                title="Voltar"
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {error ? (
            <div className="apl-alert" role="alert">
              {error}
            </div>
          ) : null}

          {!isLoadingDias && diasEvento.length > 0 ? (
            <nav className="apl-day-tabs" aria-label="Dias do evento">
              {diasEvento.map((item) => (
                <Link
                  key={item.data}
                  to={`/private/adm/eventos/${item.dia}`}
                  className={`apl-day-tab${
                    dia === item.dia ? " is-active" : ""
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          ) : null}

          {!isLoading && !isLoadingDias && eventos.length > 0 ? (
            <section
              className="apl-metrics"
              aria-label="Indicadores de conteúdos e esportes"
            >
              <div className="apl-metric apl-metric--total">
                <span className="apl-metric-value">{indicadores.total}</span>
                <span className="apl-metric-label">
                  {indicadores.total === 1 ? "Evento" : "Eventos no dia"}
                </span>
              </div>
              <div className="apl-metric">
                <span className="apl-metric-value">{diaAtivoLabel}</span>
                <span className="apl-metric-label">Dia selecionado</span>
              </div>
            </section>
          ) : null}

          {isLoading || isLoadingDias ? (
            <div className="apl-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>
                  {isLoadingDias
                    ? "Carregando dias do evento…"
                    : "Carregando conteúdos e esportes…"}
                </span>
              </div>
            </div>
          ) : (
            <section
              className="apl-card"
              aria-label="Tabela de conteúdos e esportes"
            >
              <div className="apl-table-wrap">
                {eventosFiltrados.length === 0 ? (
                  <div className="apl-empty">
                    <p className="apl-empty-title">
                      {eventos.length === 0
                        ? "Nenhum evento cadastrado para essa data"
                        : "Nenhum resultado"}
                    </p>
                    <p style={{ margin: 0 }}>
                      {eventos.length === 0
                        ? "Cadastre um evento ou selecione outro dia."
                        : "Ajuste a busca para ver eventos."}
                    </p>
                  </div>
                ) : (
                  <table className="apl-table">
                    <thead>
                      <tr>
                        <th scope="col">Horário</th>
                        <th scope="col">Evento</th>
                        <th scope="col">Tipo</th>
                        <th scope="col">Inscritos</th>
                        <th scope="col" style={{ textAlign: "right" }}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventosFiltrados.map((evento) => {
                        const dtInicio = horarioEvento(evento.dt_hora_inicio);
                        const dtFim = horarioEvento(evento.dt_hora_fim);
                        const inscritos = qtdInscritos(evento);

                        return (
                          <tr key={evento.id}>
                            <td>
                              <span className="apl-stat">
                                {dtInicio} – {dtFim}
                              </span>
                            </td>
                            <td className="apl-cell-name">
                              {evento.nome || "—"}
                            </td>
                            <td>
                              {evento.tipo ? (
                                <span className="apl-tag-type">
                                  {LABELS_TIPO[evento.tipo] || evento.tipo}
                                </span>
                              ) : (
                                <span className="apl-stat-muted">—</span>
                              )}
                            </td>
                            <td>
                              <span className="apl-stat">{inscritos}</span>
                            </td>
                            <td>
                              <div className="apl-actions">
                                {isAdmin && (
                                  <Link
                                    to={`/private/adm/eventos-editar/${evento.id}`}
                                  >
                                    <FaEdit size={12} aria-hidden />
                                    Editar
                                  </Link>
                                )}
                                <button
                                  type="button"
                                  className="apl-action-btn"
                                  onClick={() => handleModalInscritos(evento)}
                                >
                                  <FaUsers size={12} aria-hidden />
                                  Inscritos
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {eventosFiltrados.length > 0 ? (
                <footer className="apl-footer">
                  <span>
                    Exibindo{" "}
                    <strong>{eventosFiltrados.length}</strong>{" "}
                    {eventosFiltrados.length === 1 ? "evento" : "eventos"}
                    {filtro ? (
                      <>
                        {" "}
                        <span className="apl-stat-muted">
                          (de {eventos.length} no dia)
                        </span>
                      </>
                    ) : null}
                  </span>
                  <span>Dia {diaAtivoLabel}</span>
                </footer>
              ) : null}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmEventos;
